import Bowser from 'bowser';
import mixpanel from 'mixpanel-browser';

import { getAppVersion } from '../utils/appVersion';
import { LanguageUtils } from '../utils/translations';
import { consoleError } from '../utils/utils';

// https://docs.mixpanel.com/docs/data-structure/property-reference/data-type
// Date is supported as either a string or a number (timestamp)
export type PrimitiveEventData = string | number | boolean | null;
export type ObjectEventData = Record<string, PrimitiveEventData>;
export type EventData = PrimitiveEventData | ObjectEventData | PrimitiveEventData[];
export type EventProps = { [key: string]: EventData };

export type PropsWithProviderId = EventProps & { providerId: number };
type PropsWithLeadId = EventProps & { leadToken: string };

export enum MixpanelEventType {
  SIDE_NAV_PANEL_MODULE_SELECTED = 'side_navigation_panel_module_selected',
  TAB_CLICKED = 'module_tab_selected',
  METRIC_TILE_SELECTED = 'metric_tile_selected',
  PERIOD_SELECTOR_OPENED = 'period_selector_opened',
  PERIOD_SELECTED = 'period_selected',
  HOME_WIDGET_CLICKED = 'home_widget_clicked',
  DEEP_LINK_OPENED = 'deep_link_opened',
  VENUE_SELECTOR_OPENED = 'venue_selector_opened',
  VENUES_SELECTED = 'venues_selected',
  VENUE_SEARCH_PERFORMED = 'venue_search_performed',
  ACTION_LINK_CLICKED = 'action_link_clicked',
  TABLE_COLUMN_SORTED = 'table_column_sorted',
  TABLE_PAGE_CHANGED = 'table_page_changed',
  NAVIGATION_PATH_CHANGE = 'navigation_path_change',
  INSIGHT_CARD_SHOWN = 'insight_card_shown',
  INSIGHT_CARD_DISMISSED = 'insight_card_dismissed',
}
/**
 * Returns set of properties, required for tracking details of user's environment.
 * Device & Browser in particular.
 */
const getDeviceInfo = (() => {
  const refresh = () => {
    const browser = Bowser.parse(window.navigator.userAgent);

    return {
      browser_name: browser.browser.name,
      platform_type: browser.platform.type,
      platform_model: browser.platform.model,
      platform_vendor: browser.platform.vendor,
      os_name: browser.os.name,
      os_version: browser.os.version,
      device_orientation: window.matchMedia('(orientation: portrait)').matches
        ? 'portrait'
        : 'landscape',
    };
  };

  let value = refresh();

  // We don't want to call `refresh` on every function call
  let shouldBeUpdated = false;

  // Props needs to be refreshed on browser rotation
  window.addEventListener('orientationchange', () => {
    shouldBeUpdated = true;
  });

  return () => {
    if (shouldBeUpdated) {
      value = refresh();
      shouldBeUpdated = false;
    }
    return value;
  };
})();

/**
 * We don't want `track` method to be part of Mixpanel's public methods, and
 * eslint has some schizophrenic setting where he wants to have two conflicting things:
 *  - mock `track` as a method of MixpanelService because it is there
 *  - do not mock `private` method
 * this is why we will keep this piece of code as a separate function
 */
function track(name: string, props: EventProps) {
  if (!process.env.REACT_APP_MIXPANEL_TOKEN) {
    return;
  }
  try {
    const appLocale = LanguageUtils.getAppLocaleWithFallback();
    const { appVersion, moduleName, moduleVersion } = getAppVersion();

    mixpanel.track(name, {
      ...getDeviceInfo(),
      ...props,
      appLocale,
      appVersion,
      moduleName,
      moduleVersion,
    });
  } catch (e) {
    consoleError('Failed to track MixPanel event', e);
  }
}

export class MixpanelService {
  constructor() {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN || '', {
      api_host: process.env.REACT_APP_MIXPANEL_API_HOST,
    });
  }

  public identify(distinctId: string) {
    mixpanel.identify(distinctId);
  }

  public setUserProfile(props: EventProps) {
    if (!process.env.REACT_APP_MIXPANEL_TOKEN) {
      return;
    }
    mixpanel.people.set(props);
  }

  public reset() {
    mixpanel.reset();
  }

  public alias(newId: string, oldId: string) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::alias] ', newId, oldId);
    }
    mixpanel.alias(newId, oldId);
  }

  /**
   * Majority of events planned to be tracked, are required to pass providerId
   * To mitigate human factor, we will use this method for tracking any mixpanel events,
   * until we get to the case that conflicts with this assumption
   */
  public trackProviderEvent(name: string, props: PropsWithProviderId) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::trackProviderEvent] ', name, JSON.stringify(props, null, 2));
    }
    track(name, props);
  }

  public trackAccountEvent(name: string, props: EventProps): void {
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::trackAccountEvent] ', name, JSON.stringify(props, null, 2));
    }
    track(name, props);
  }

  public trackLeadEvent(name: string, props: PropsWithLeadId) {
    if (!props.leadToken) {
      return;
    }
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::trackLeadEvent] ', name, JSON.stringify(props, null, 2));
    }
    track(name, props);
  }
}
